'use client';

import { STATUS, SURFACE, BORDER } from '@/constants/colors';
import { memo, useState } from 'react';
import { usePathname } from 'next/navigation';
import Image from 'next/image';
import {
  Box,
  Drawer,
  Stack,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  alpha,
} from '@mui/material';
import {
  Home,
  History,
  LocalShipping,
  AccountCircle,
  HelpOutline,
  Logout,
  Close,
  Dashboard,
  LocationOn,
  BarChart,
  AccountBalanceWallet,
  Receipt,
  Gavel,
  Inventory2,
} from '@mui/icons-material';
import Link from 'next/link';
import LogoutConfirmationDialog from '../components/common/LogoutConfirmationDialog';
import { brandingConfig } from '@/config/branding';
/**
 * Mobile Sidebar Component - Slide-out drawer for mobile layouts
 */
const MobileSidebar = memo(({ user, isOpen = false, onClose, onLogout }) => {
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const pathname = usePathname();
  const isBusiness = user?.userType?.toLowerCase() === 'business';

  // Clean, professional styling - NO gradients
  const iconColor = '#666';
  const activeBgColor = '#fff';
  const activeColor = isBusiness ? '#f59e0b' : '#10b981';

  const isActive = (path) => {
    if (!pathname) return false;
    return pathname === path || pathname.startsWith(path + '/');
  };

  // Build navigation items based on user type AND business setup status
  let mainNavItems = [];

  if (isBusiness) {
    if (user?.businessSetupCompleted) {
      // Complete business profile - show all items
      mainNavItems = [
        {
          key: 'profile',
          label: 'Profile',
          icon: AccountCircle,
          path: '/profile',
        },
        {
          key: 'business-dashboard',
          label: 'Dashboard',
          icon: Dashboard,
          path: '/business-dashboard',
        },
        {
          key: 'my-pricing',
          label: 'My Pricing',
          icon: Receipt,
          path: '/my-pricing',
        },
        {
          key: 'bulk-booking',
          label: 'Bulk Booking',
          icon: Inventory2,
          path: '/bulk-booking',
        },
        {
          key: 'saved-addresses',
          label: 'Addresses',
          icon: LocationOn,
          path: '/saved-addresses',
        },
        {
          key: 'activeParcels',
          label: 'Active Parcels',
          icon: LocalShipping,
          path: '/activeParcels',
        },
        { key: 'history', label: 'History', icon: History, path: '/history' },
        {
          key: 'invoices',
          label: 'Invoices',
          icon: Receipt,
          path: '/invoices',
        },
        {
          key: 'analytics',
          label: 'Analytics',
          icon: BarChart,
          path: '/business-analytics',
        },
      ];
    } else {
      // Incomplete business profile - ONLY show profile (they'll be redirected from other pages anyway)
      mainNavItems = [
        {
          key: 'profile',
          label: 'Profile',
          icon: AccountCircle,
          path: '/profile',
        },
      ];
    }
  } else {
    // Individual users - show all items
    mainNavItems = [
      {
        key: 'profile',
        label: 'Profile',
        icon: AccountCircle,
        path: '/profile',
      },
      { key: 'home', label: 'Home', icon: Home, path: '/home' },
      {
        key: 'activeParcels',
        label: 'Active Parcels',
        icon: LocalShipping,
        path: '/activeParcels',
      },
      { key: 'history', label: 'History', icon: History, path: '/history' },
      { key: 'invoices', label: 'Invoices', icon: Receipt, path: '/invoices' },
    ];
  }

  const bottomNavItems = [
    { key: 'support', label: 'Support', icon: HelpOutline, path: '/support' },
    { key: 'terms', label: 'Terms & Conditions', icon: Gavel, path: '/terms' },
  ];

  // Only add wallet for:
  // 1. Individual users (always)
  // 2. Business users with completed profiles
  const showWallet = !isBusiness || user?.businessSetupCompleted;
  if (showWallet) {
    bottomNavItems.unshift({
      key: 'wallet',
      label: 'Wallet',
      icon: AccountBalanceWallet,
      path: '/wallet',
    });
  }

  return (
    <Drawer
      anchor="left"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 300,
          bgcolor: '#fafafa',
          borderRight: '1px solid #e0e0e0',
        },
      }}
    >
      {/* Clean Header */}
      <Box
        sx={{
          bgcolor: '#fff',
          p: 3,
          pb: 2,
          position: 'relative',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            width: 36,
            height: 36,
            color: '#666',
            '&:hover': { bgcolor: SURFACE.background },
          }}
          aria-label="Close menu"
        >
          <Close sx={{ fontSize: 20 }} />
        </IconButton>

        {/* Terrakite Logo and Title */}
        <Box
          onClick={() => {
            onClose();
            router.push('/');
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            mb: 2,
          }}
        >
          <Image
            src="/assets/terrakite-teal-logo.png"
            alt={brandingConfig.assets.logoAlt}
            width={140}
            height={40}
            style={{ objectFit: 'contain' }}
          />
        </Box>

        {user && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              borderRadius: 2,
              p: 1.5,
              bgcolor: '#f8f8f8',
              border: '1px solid #e8e8e8',
            }}
          >
            <Avatar
              src={user.avatar}
              sx={{
                width: 48,
                height: 48,
                bgcolor: BORDER.light,
                color: '#666',
                fontSize: 18,
                fontWeight: 700,
              }}
            >
              {user.userName?.[0] || 'U'}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a' }}>
                {user.userName || 'User'}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: '#666',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user.userEmail}
              </Typography>
              {isBusiness && (
                <Box
                  sx={{
                    display: 'inline-block',
                    mt: 0.5,
                    px: 1,
                    py: 0.25,
                    bgcolor: '#fff3e0',
                    borderRadius: 1,
                    fontSize: 9,
                    fontWeight: 700,
                    color: '#f57c00',
                    letterSpacing: 0.5,
                    border: '1px solid #ffe0b2',
                  }}
                >
                  BUSINESS
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* Main Navigation */}
      <Box sx={{ flex: 1, py: 2 }}>
        {/* New Booking Button */}
        <Box sx={{ px: 2, mb: 2 }}>
          <Link href="/booking" passHref legacyBehavior prefetch>
            <ListItemButton
              component="a"
              onClick={onClose}
              sx={{
                bgcolor: '#000',
                color: 'white',
                borderRadius: 2,
                py: 1.5,
                px: 2,
                justifyContent: 'center',
                '&:hover': {
                  bgcolor: '#333',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <Box component="span" sx={{ fontSize: 20, mr: 1 }}>
                ➕
              </Box>
              <Typography sx={{ fontWeight: 600, fontSize: 15 }}>New Booking</Typography>
            </ListItemButton>
          </Link>
        </Box>

        <List disablePadding sx={{ px: 1 }}>
          {mainNavItems.map((item) => {
            const active = isActive(item.path);
            const isBulkBooking = item.key === 'bulk-booking';
            const bulkBookingPurple = '#8b5cf6';
            return (
              <ListItem key={item.key} disablePadding sx={{ mb: 0.5 }}>
                <Link href={item.path} passHref legacyBehavior prefetch>
                  <ListItemButton
                    component="a"
                    onClick={onClose}
                    sx={{
                      py: 1.25,
                      px: 2,
                      borderRadius: 2,
                      textDecoration: 'none',
                      bgcolor: active ? alpha(activeColor, 0.08) : 'transparent',
                      borderLeft: active ? `3px solid ${activeColor}` : '3px solid transparent',
                      position: 'relative',
                      ...(isBulkBooking &&
                        !active && {
                          background: `linear-gradient(135deg, ${alpha(bulkBookingPurple, 0.12)} 0%, ${alpha(activeColor, 0.06)} 100%)`,
                          border: `2px solid ${alpha(bulkBookingPurple, 0.25)}`,
                          boxShadow: `0 0 12px ${alpha(bulkBookingPurple, 0.25)}`,
                          '&::after': {
                            content: '"PRO"',
                            position: 'absolute',
                            top: -6,
                            right: 8,
                            fontSize: '0.6rem',
                            fontWeight: 900,
                            color: '#fff',
                            bgcolor: bulkBookingPurple,
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 1,
                            boxShadow: `0 2px 4px ${alpha(bulkBookingPurple, 0.3)}`,
                          },
                        }),
                      '&:hover': {
                        bgcolor: active ? alpha(activeColor, 0.12) : '#fff',
                        ...(isBulkBooking && {
                          boxShadow: `0 0 16px ${alpha(activeColor, 0.35)}`,
                        }),
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <item.icon
                        sx={{
                          fontSize: 20,
                          color:
                            isBulkBooking && !active
                              ? bulkBookingPurple
                              : active
                                ? activeColor
                                : active
                                  ? activeColor
                                  : iconColor,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: isBulkBooking ? 600 : active ? 600 : 400,
                        color:
                          isBulkBooking && !active
                            ? bulkBookingPurple
                            : active
                              ? activeColor
                              : iconColor,
                      }}
                    />
                  </ListItemButton>
                </Link>
              </ListItem>
            );
          })}
        </List>

        <Divider sx={{ my: 1 }} />

        <List disablePadding sx={{ px: 1 }}>
          {bottomNavItems.map((item) => {
            const active = isActive(item.path);
            return (
              <ListItem key={item.key} disablePadding sx={{ mb: 0.5 }}>
                <Link href={item.path} passHref legacyBehavior prefetch>
                  <ListItemButton
                    component="a"
                    onClick={onClose}
                    sx={{
                      py: 1.25,
                      px: 2,
                      borderRadius: 2,
                      textDecoration: 'none',
                      bgcolor: active ? alpha(activeColor, 0.08) : 'transparent',
                      borderLeft: active ? `3px solid ${activeColor}` : '3px solid transparent',
                      '&:hover': {
                        bgcolor: active ? alpha(activeColor, 0.12) : '#fff',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <item.icon
                        sx={{
                          fontSize: 20,
                          color: active ? activeColor : iconColor,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: active ? 600 : 400,
                        color: active ? activeColor : iconColor,
                      }}
                    />
                  </ListItemButton>
                </Link>
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Logout - Clean and Simple */}
      <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#fff' }}>
        <ListItemButton
          onClick={() => setShowLogoutDialog(true)}
          disabled={isLoggingOut}
          sx={{
            bgcolor: SURFACE.background,
            borderRadius: 2,
            justifyContent: 'center',
            py: 1.5,
            border: '1px solid #e0e0e0',
            '&:hover': { bgcolor: BORDER.light },
          }}
        >
          <Logout sx={{ color: '#666', fontSize: 20, mr: 1 }} />
          <Typography sx={{ color: '#666', fontWeight: 600, fontSize: 15 }}>Sign Out</Typography>
        </ListItemButton>
      </Box>

      {/* Logout Confirmation Dialog */}
      <LogoutConfirmationDialog
        open={showLogoutDialog}
        onClose={() => setShowLogoutDialog(false)}
        onConfirm={async () => {
          setIsLoggingOut(true);
          await onLogout();
          setShowLogoutDialog(false);
          setIsLoggingOut(false);
        }}
        loading={isLoggingOut}
      />
    </Drawer>
  );
});

MobileSidebar.displayName = 'MobileSidebar';

export default MobileSidebar;
